import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";

interface Props {
  onResult: (text: string) => void;
}

export function QrScanner({ onResult }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let frame = 0;
    let stopped = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    function scan() {
      if (stopped) return;
      const video = videoRef.current;
      if (ctx && video && video.readyState >= video.HAVE_CURRENT_DATA) {
        // Fürs Erkennen reicht ein verkleinertes Bild – spart deutlich CPU
        const scale = Math.min(
          1,
          480 / Math.max(video.videoWidth, video.videoHeight),
        );
        canvas.width = Math.floor(video.videoWidth * scale);
        canvas.height = Math.floor(video.videoHeight * scale);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const result = jsQR(image.data, image.width, image.height);
        if (result?.data) {
          stopped = true;
          onResultRef.current(result.data);
          return;
        }
      }
      frame = requestAnimationFrame(scan);
    }

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          window.isSecureContext
            ? "Dieser Browser unterstützt keinen Kamera-Zugriff."
            : "Kamera-Zugriff im Browser geht nur über HTTPS (oder localhost). " +
                "Scanne den QR-Code stattdessen mit der Kamera-App deines Handys.",
        );
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
      } catch {
        setError(
          "Kamera-Zugriff verweigert oder keine Kamera gefunden. " +
            "Alternativ kannst du den QR-Code mit der Kamera-App scannen.",
        );
        return;
      }
      if (stopped) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        // Abbruch beim Schließen des Dialogs
      }
      scan();
    }

    void start();
    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      if (stream) for (const track of stream.getTracks()) track.stop();
    };
  }, []);

  if (error) {
    return <p className="scanner-error">{error}</p>;
  }
  return (
    <div className="scanner-box">
      <video ref={videoRef} className="scanner-video" playsInline muted />
      <p className="muted">Halte den QR-Code vor die Kamera.</p>
    </div>
  );
}
